/* eslint-disable */
import { useState } from "react";
import { loadFromStorage, useToast } from "./Auth";
import { saveDossierToCloud,} from "./SupabaseSync";
import { exportDossierPDF, ExportButton } from "./ExportPDF";
import { COMPETITIONS, CLUBS, buildOppositionBrief, fetchTeamForm } from "./FootballData";

/* ═══════════════════════════════════════════════════════════
   FOOTY ANALYST — PRE-MATCH OPPOSITION DOSSIER
   Build opponent profile · Analyse threats · Generate dossier
   4-day training plan · Player briefings · Export ready
═══════════════════════════════════════════════════════════ */

const OD_CSS = `
.od-wrap { font-family:'DM Sans',sans-serif; background:var(--cream); min-height:100%; }

/* HERO */
.od-hero { background:var(--darkred); border-bottom:3px solid var(--mustard); padding:1.8rem; position:relative; overflow:hidden; }
.od-hero::before { content:''; position:absolute; inset:0; background:repeating-linear-gradient(0deg,transparent,transparent 30px,rgba(0,0,0,0.06) 30px,rgba(0,0,0,0.06) 31px); pointer-events:none; }
.od-hero-inner { position:relative; z-index:1; display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:1rem; }
.od-hero-title { font-family:'Bebas Neue',sans-serif; font-size:2rem; letter-spacing:3px; color:var(--white); line-height:1; }
.od-hero-sub { font-size:0.75rem; color:rgba(255,255,255,0.55); font-style:italic; font-weight:300; margin-top:0.25rem; max-width:520px; line-height:1.6; }

/* LAYOUT */
.od-layout { display:grid; grid-template-columns:340px 1fr; gap:0; min-height:calc(100vh - 180px); }
.od-sidebar { background:var(--white); border-right:2px solid var(--rule); display:flex; flex-direction:column; overflow-y:auto; }
.od-main { overflow-y:auto; background:var(--cream); }

/* SIDEBAR */
.od-sb-section { padding:1rem; border-bottom:1.5px solid var(--rule); }
.od-sb-title { font-family:'Bebas Neue',sans-serif; font-size:0.88rem; letter-spacing:2.5px; color:var(--ink); margin-bottom:0.75rem; }
.od-label { display:block; font-size:0.58rem; font-weight:800; letter-spacing:2px; text-transform:uppercase; color:var(--muted); margin-bottom:0.32rem; margin-top:0.65rem; }
.od-label:first-child { margin-top:0; }
.od-input { width:100%; background:var(--cream); border:1.5px solid var(--rule); border-bottom:2px solid var(--ink); padding:0.58rem 0.8rem; color:var(--ink); font-family:'DM Sans',sans-serif; font-size:0.82rem; font-weight:500; outline:none; }
.od-input:focus { border-bottom-color:var(--teal); }
.od-select { width:100%; background:var(--cream); border:1.5px solid var(--rule); border-bottom:2px solid var(--ink); padding:0.58rem 0.8rem; color:var(--ink); font-family:'DM Sans',sans-serif; font-size:0.82rem; font-weight:500; outline:none; cursor:pointer; }

/* Style selector */
.od-style-grid { display:grid; grid-template-columns:1fr 1fr; gap:0.4rem; }
.od-style-btn { padding:0.42rem 0.5rem; text-align:center; font-family:'DM Sans',sans-serif; font-size:0.62rem; font-weight:800; letter-spacing:1px; text-transform:uppercase; cursor:pointer; border:1.5px solid var(--rule); color:var(--muted); background:transparent; transition:all 0.18s; line-height:1.3; }
.od-style-btn:hover { border-color:var(--darkred); color:var(--darkred); }
.od-style-btn.on { background:var(--darkred); border-color:var(--darkred); color:var(--white); }

/* Threat toggles */
.od-threat-list { display:flex; flex-direction:column; gap:0.3rem; }
.od-threat-btn { display:flex; align-items:center; gap:0.6rem; padding:0.42rem 0.65rem; border:1.5px solid var(--rule); cursor:pointer; transition:all 0.18s; text-align:left; width:100%; background:transparent; }
.od-threat-btn:hover { border-color:var(--darkred); }
.od-threat-btn.on { background:var(--darkred3); border-color:var(--darkred); }
.od-threat-dot { width:8px; height:8px; border-radius:50%; background:var(--rule); flex-shrink:0; }
.od-threat-btn.on .od-threat-dot { background:var(--darkred); }
.od-threat-label { font-family:'DM Sans',sans-serif; font-size:0.72rem; font-weight:600; color:var(--ink); }

/* Generate button */
.od-generate { width:100%; padding:0.95rem; background:var(--darkred); color:var(--white); border:2px solid var(--darkred); font-family:'DM Sans',sans-serif; font-weight:800; font-size:0.82rem; letter-spacing:2.5px; text-transform:uppercase; cursor:pointer; transition:all 0.2s; }
.od-generate:hover { background:var(--darkred2); border-color:var(--darkred2); }

/* Saved dossiers */
.od-saved-item { display:flex; align-items:center; gap:0.6rem; padding:0.5rem 0.7rem; border:1px solid var(--rule); margin-bottom:0.3rem; cursor:pointer; transition:all 0.18s; background:var(--cream2); }
.od-saved-item:hover { border-color:var(--darkred); background:var(--darkred3); }
.od-saved-name { font-size:0.78rem; font-weight:700; color:var(--ink); flex:1; }
.od-saved-date { font-size:0.6rem; color:var(--muted); font-style:italic; }

/* MAIN DOSSIER */
.od-content { padding:1.5rem; }

/* Dossier header */
.od-dossier-header { background:var(--darkred); border:2px solid var(--ink); padding:1.5rem; margin-bottom:1rem; position:relative; overflow:hidden; }
.od-dossier-header::before { content:'CONFIDENTIAL'; position:absolute; top:50%; right:-20px; transform:translateY(-50%) rotate(-15deg); font-family:'Bebas Neue',sans-serif; font-size:3rem; letter-spacing:4px; color:rgba(255,255,255,0.04); pointer-events:none; }
.od-dossier-vs { font-family:'Bebas Neue',sans-serif; font-size:0.75rem; letter-spacing:3px; color:rgba(255,255,255,0.45); text-transform:uppercase; margin-bottom:0.3rem; }
.od-dossier-opponent { font-family:'Bebas Neue',sans-serif; font-size:2.8rem; letter-spacing:3px; color:var(--white); line-height:1; }
.od-dossier-meta { font-size:0.72rem; color:rgba(255,255,255,0.55); margin-top:0.3rem; font-weight:300; }
.od-dossier-tags { display:flex; gap:0.4rem; flex-wrap:wrap; margin-top:0.75rem; }
.od-dossier-tag { font-size:0.6rem; font-weight:800; padding:0.14rem 0.5rem; letter-spacing:1.5px; text-transform:uppercase; border:1.5px solid rgba(255,255,255,0.25); color:rgba(255,255,255,0.75); }
.od-dossier-tag.danger { background:rgba(255,255,255,0.12); border-color:var(--mustard); color:var(--mustard); }

/* Dossier grid */
.od-grid-2 { display:grid; grid-template-columns:1fr 1fr; gap:1rem; margin-bottom:1rem; }
.od-grid-3 { display:grid; grid-template-columns:1fr 1fr 1fr; gap:1rem; margin-bottom:1rem; }
.od-panel { background:var(--white); border:1.5px solid var(--rule); padding:1.1rem; }
.od-panel-title { font-family:'Bebas Neue',sans-serif; font-size:0.92rem; letter-spacing:2px; color:var(--ink); margin-bottom:0.8rem; padding-bottom:0.55rem; border-bottom:1.5px solid var(--rule); display:flex; align-items:center; justify-content:space-between; }
.od-chip { font-family:'DM Sans',sans-serif; font-size:0.54rem; font-weight:800; padding:0.14rem 0.46rem; letter-spacing:2px; text-transform:uppercase; }
.od-chip-red { background:var(--darkred); color:var(--white); }
.od-chip-teal { background:var(--teal); color:var(--white); }
.od-chip-gold { background:var(--mustard); color:var(--ink); }

/* Threat items */
.od-threat-item { display:flex; align-items:flex-start; gap:0.6rem; padding:0.55rem 0; border-bottom:1px solid var(--rule2); }
.od-threat-item:last-child { border-bottom:none; }
.od-threat-icon { font-size:1.1rem; flex-shrink:0; }
.od-threat-content { flex:1; }
.od-threat-title { font-size:0.82rem; font-weight:800; color:var(--ink); margin-bottom:0.15rem; }
.od-threat-desc { font-size:0.73rem; color:var(--muted); font-style:italic; font-weight:300; line-height:1.6; }
.od-threat-level { font-size:0.58rem; font-weight:800; padding:0.1rem 0.4rem; letter-spacing:1.5px; text-transform:uppercase; flex-shrink:0; }
.od-threat-level.high { background:var(--darkred); color:var(--white); }
.od-threat-level.med  { background:var(--mustard); color:var(--ink); }
.od-threat-level.low  { background:var(--teal); color:var(--white); }

/* Instruction items */
.od-instruction { display:flex; align-items:flex-start; gap:0.55rem; padding:0.48rem 0; border-bottom:1px solid var(--rule2); font-size:0.78rem; color:var(--ink2); line-height:1.55; font-weight:500; }
.od-instruction:last-child { border-bottom:none; }
.od-instruction::before { content:'→'; color:var(--teal); font-weight:800; flex-shrink:0; margin-top:0.05rem; }
.od-instruction.defensive::before { color:var(--darkred); }
.od-instruction.attacking::before { color:#16a34a; }

/* Set piece section */
.od-setpiece { background:var(--sand4); border:1.5px solid var(--rule); border-left:4px solid var(--mustard); padding:0.75rem 0.9rem; margin-bottom:0.5rem; }
.od-setpiece-title { font-size:0.8rem; font-weight:800; color:var(--ink); margin-bottom:0.2rem; }
.od-setpiece-desc { font-size:0.72rem; color:var(--ink2); font-weight:300; font-style:italic; line-height:1.6; }

/* Player briefing */
.od-player-briefing { display:flex; align-items:flex-start; gap:0.75rem; padding:0.7rem 0; border-bottom:1px solid var(--rule2); }
.od-player-briefing:last-child { border-bottom:none; }
.od-pb-av { width:36px; height:36px; border-radius:50%; border:2px solid var(--ink); display:flex; align-items:center; justify-content:center; font-size:0.65rem; font-weight:800; color:var(--white); flex-shrink:0; }
.od-pb-info { flex:1; }
.od-pb-name { font-size:0.85rem; font-weight:800; color:var(--ink); }
.od-pb-pos { font-size:0.58rem; font-weight:800; padding:0.1rem 0.38rem; letter-spacing:1.5px; text-transform:uppercase; display:inline-block; margin-bottom:0.3rem; }
.od-pb-task { font-size:0.75rem; color:var(--ink2); font-weight:400; line-height:1.6; }
.od-pb-focus { font-size:0.68rem; color:var(--teal); font-weight:700; font-style:italic; margin-top:0.2rem; }

/* 4-day training plan */
.od-week { display:grid; grid-template-columns:repeat(4,1fr); gap:0.75rem; margin-bottom:1rem; }
.od-day { background:var(--white); border:1.5px solid var(--rule); overflow:hidden; }
.od-day-head { padding:0.7rem 0.85rem; border-bottom:1.5px solid var(--rule); }
.od-day-name { font-family:'Bebas Neue',sans-serif; font-size:1rem; letter-spacing:2px; color:var(--ink); }
.od-day-theme { font-size:0.62rem; font-weight:700; color:var(--darkred); letter-spacing:1px; margin-top:0.1rem; }
.od-day-body { padding:0.75rem 0.85rem; }
.od-day-drill { background:var(--darkred3); border:1px solid var(--rule); border-left:3px solid var(--darkred); padding:0.42rem 0.6rem; margin-bottom:0.35rem; font-size:0.72rem; font-weight:500; color:var(--ink2); line-height:1.4; }
.od-day-note { font-size:0.65rem; color:var(--muted); font-style:italic; font-weight:300; margin-top:0.5rem; line-height:1.55; }
.od-intensity-bar { height:4px; margin-top:0.6rem; border-radius:2px; }

/* Formation display */
.od-pitch { background:linear-gradient(180deg,#2d5a32,#357a3d,#2d5a32); position:relative; height:200px; border-radius:2px; overflow:hidden; margin-bottom:0.75rem; }
.od-pitch svg { position:absolute; inset:0; width:100%; height:100%; }
.od-pitch-dot { position:absolute; transform:translate(-50%,-50%); }
.od-pitch-dot-inner { width:28px; height:28px; border-radius:50%; display:flex; flex-direction:column; align-items:center; justify-content:center; font-size:0.5rem; font-weight:800; border:2px solid rgba(255,255,255,0.5); }
.od-pitch-dot-inner.us { background:var(--teal); color:var(--white); }
.od-pitch-dot-inner.them { background:var(--darkred); color:var(--white); }

/* Key battles */
.od-battle { display:flex; align-items:center; gap:0; margin-bottom:0.5rem; }
.od-battle-player { flex:1; padding:0.5rem 0.65rem; background:var(--teal4); border:1px solid var(--teal); }
.od-battle-name { font-size:0.78rem; font-weight:800; color:var(--ink); }
.od-battle-pos { font-size:0.58rem; color:var(--teal); font-weight:700; letter-spacing:1px; text-transform:uppercase; }
.od-battle-vs { padding:0.5rem 0.6rem; background:var(--ink); color:var(--white); font-family:'Bebas Neue',sans-serif; font-size:0.75rem; letter-spacing:1px; text-align:center; flex-shrink:0; }
.od-battle-opp { flex:1; padding:0.5rem 0.65rem; background:var(--darkred3); border:1px solid var(--darkred); text-align:right; }
.od-battle-opp-name { font-size:0.78rem; font-weight:800; color:var(--ink); }
.od-battle-opp-threat { font-size:0.58rem; color:var(--darkred); font-weight:700; letter-spacing:1px; }

/* Export */
.od-export { width:100%; padding:0.95rem; background:var(--darkred); color:var(--white); border:2px solid var(--darkred); font-family:'DM Sans',sans-serif; font-weight:800; font-size:0.8rem; letter-spacing:2.5px; text-transform:uppercase; cursor:pointer; transition:all 0.2s; margin-top:1rem; }
.od-export:hover { background:var(--ink); border-color:var(--ink); }

/* Empty */
.od-empty { text-align:center; padding:4rem 2rem; }
.od-empty-icon { font-size:3.5rem; margin-bottom:1rem; }
.od-empty-title { font-family:'Bebas Neue',sans-serif; font-size:1.8rem; letter-spacing:2px; margin-bottom:0.5rem; }
.od-empty-sub { font-size:0.85rem; color:var(--muted); font-style:italic; font-weight:300; max-width:400px; margin:0 auto; line-height:1.7; }

@media(max-width:1000px) {
  .od-layout { grid-template-columns:1fr; }
  .od-week { grid-template-columns:1fr 1fr; }
  .od-grid-2, .od-grid-3 { grid-template-columns:1fr; }
}
`;

/* ═══════════════════════════════════════════
   OPPOSITION INTELLIGENCE DATABASE
═══════════════════════════════════════════ */

const OPP_STYLES = [
  {id:"low-block",    label:"Low Block",       icon:"🔒", desc:"Sit deep, compact, counter"},
  {id:"high-press",   label:"High Press",      icon:"⚡", desc:"Press all over the pitch"},
  {id:"possession",   label:"Possession",      icon:"🔄", desc:"Dominate the ball"},
  {id:"direct",       label:"Direct & Physical",icon:"💪",desc:"Long ball, second balls"},
  {id:"counter",      label:"Counter-attack",  icon:"🏹", desc:"Absorb and hit on the break"},
  {id:"mid-block",    label:"Mid-block Press",  icon:"🎯", desc:"Press in waves from mid"},
];

const OPP_FORMATIONS = ["4-4-2","4-3-3","4-2-3-1","3-5-2","3-4-3","5-3-2","4-1-4-1","4-4-2 Diamond"];

const THREAT_OPTIONS = [
  "Dangerous set pieces", "Pace up front", "Strong aerial presence",
  "Quality from wide areas", "Creative No.10", "High press intensity",
  "Clinical striker", "Physical midfield", "Quick transitions",
  "Long throw specialist", "Dead ball specialist", "Strong defensively"
];

/* ═══════════════════════════════════════════
   DOSSIER INTELLIGENCE ENGINE
═══════════════════════════════════════════ */

const DOSSIER_DATA = {
  "low-block": {
    colour: "#8b2a2a",
    summary: "They will sit in a compact 4-4-2 or 5-4-1 and frustrate you. Their goal is 0-0 at half time and a set piece or counter in the second half. Patience is your greatest weapon.",
    theirPrinciples: [
      "Two banks of four — maximum 10 metres between the lines",
      "No unnecessary pressing — conserve energy and stay organised",
      "Win the second ball after clearances — the defensive midfielder wins possession",
      "Set pieces are their best chance to score — treat every corner as a danger",
      "Counter-attack at pace when the ball is won — release the striker immediately",
    ],
    threats: [
      {icon:"🎯",title:"Set Piece Danger",desc:"Their primary threat. Corners and free kicks are trained extensively. Tall defenders make late runs. Your zonal marking must be perfect.",level:"high"},
      {icon:"🏹",title:"Counter-attack",desc:"When they win the ball, they go immediately. Pace in behind your defensive line is the threat. Never over-commit with your full-backs.",level:"high"},
      {icon:"💪",title:"Second Ball",desc:"After a clearance, they win the second ball and maintain pressure. Their midfield is physical and will out-battle a tired team.",level:"med"},
    ],
    defensiveInstructions: [
      "Never panic — a 0-0 at half time is not a bad result against this team",
      "Maintain width — do not narrow so much that you lose the wide spaces",
      "The CDM must step forward to receive — never play backwards under pressure",
      "Full-backs attack but one CM must always cover — never leave 2v2 at the back",
      "After a corner, reset immediately — they are at their most dangerous from a quick restart",
    ],
    attackingInstructions: [
      "Be patient — circulate the ball and wait for the gap, do not force it",
      "Use width to stretch their block — get the full-backs high and pull the wingers wide",
      "The decisive pass goes through the block — a cut-back or through-ball, not around them",
      "Set pieces are your best chance — invest in delivery quality and runner timing",
      "The striker must hold the ball and bring others into play — do not chase long balls",
    ],
    setPieceDefence: [
      {title:"Corner Defence",desc:"Zonal marking — every player owns a zone. The near post player is your most important. Call clearly and step out as a unit when the ball is delivered."},
      {title:"Free Kick Wall",desc:"Four-man wall for any free kick within 25m. One player marks the post. The goalkeeper is responsible for anything over the wall."},
      {title:"Long Throw",desc:"If they have a long-throw specialist, define who wins it. Typically the biggest CB. Mark the second ball runner tightly."},
    ],
    setPieceAttack: [
      {title:"Corner Routine 1 — Near Post Flick",desc:"Near-post runner attacks the first zone. Flick-on to the far-post runner arriving late. Second ball player holds 12 yards."},
      {title:"Corner Routine 2 — Pull-back",desc:"Delivery to the edge of the box. Two runners from the penalty spot pull defenders. Third runner arrives late for the pull-back."},
      {title:"Free Kick",desc:"Delivery into the mixer when within 20m. Dummy runner peels away to leave space for the target runner arriving from deep."},
    ],
    weekPlan: [
      {day:"Tuesday",theme:"Pattern Play",intensity:60,colour:"#16a34a",drills:["Ball circulation vs low block (8v8)",	"Patience rondo — 15 passes before forward play","Width drill — full-backs and wingers combining"],note:"Light session. Technical. Focus on patience and circulating ball. No panic passing."},
      {day:"Wednesday",theme:"Breaking Lines",intensity:80,colour:"var(--darkred)",drills:["Breaking through a 4-4 block (11v10)","Cut-back and pull-back combinations","Second ball winning drill"],note:"Main physical session. Practice breaking down the block. Finishing in front of a compact defence."},
      {day:"Thursday",theme:"Set Pieces",intensity:50,colour:"var(--teal)",drills:["Corner routines — 3 variations","Defensive set piece organisation","Free kick delivery practice"],note:"Set piece focus. Both attacking and defensive. Every player knows their role."},
      {day:"Friday",theme:"Match Prep",intensity:70,colour:"var(--mustard)",drills:["Opposition shape walk-through","Final third patterns vs their formation","11v11 short match scenario"],note:"Walk through their shape. Practice the specific patterns. Sharp and focused."},
    ],
    keyBattles: [
      {us:"Your Striker",usPos:"ST",them:"Their CB pairing",themThreat:"Aerial dominance"},
      {us:"Your No.10",usPos:"CAM",them:"Their CDM",themThreat:"Physical screening"},
      {us:"Your Full-backs",usPos:"FB",them:"Their wingers",themThreat:"Counter-attack pace"},
    ],
    matchdayMessage: "A low block team wants a 0-0. Your job is to stay patient, keep the ball moving and trust that the chance will come. When it does — take it. Do not force the issue. Be the dominant team without being reckless.",
  },

  "high-press": {
    colour: "#1e40af",
    summary: "They will try to make the match chaotic — press high, win the ball in your half and score immediately. Your composure and technical quality under pressure win this match.",
    theirPrinciples: [
      "Press immediately and everywhere — no rest on the ball",
      "Win the ball high up the pitch — the shorter the distance to goal, the better",
      "High defensive line — the space behind is the risk they accept",
      "Go vertical immediately after winning the ball — no time to think",
      "Press as a coordinated unit — not as individuals",
    ],
    threats: [
      {icon:"⚡",title:"Immediate Press",desc:"The moment you receive, they press. Your technical players must be able to handle the ball under pressure. The GK is your most important press-breaker.",level:"high"},
      {icon:"🏃",title:"Transitions",desc:"After winning the ball they go immediately. The gap between your lines when you are in possession is dangerous. Defensive shape must recover instantly.",level:"high"},
      {icon:"🎯",title:"High Defensive Line",desc:"Their backline is high — there is space in behind for a quick striker. The ball over the top can be devastating against them.",level:"med"},
    ],
    defensiveInstructions: [
      "The GK must be comfortable receiving under pressure — short pass rather than hoof",
      "CBs must be brave — receive the ball even when pressed",
      "The CDM must show constantly — be the release valve for the back four",
      "When you lose the ball, get back immediately — their counter is immediate",
      "Full-backs stay disciplined — do not be dragged too high and leave gaps in behind",
    ],
    attackingInstructions: [
      "Play through the press — short, quick passes beat any press",
      "The third man combination breaks any press — use the player beyond the pressure",
      "When the press is beaten, attack immediately — they are disorganised",
      "The ball over the top is lethal against their high line — your striker must run in behind",
      "Patience — if in doubt, play back to the GK and reset. Do not force it.",
    ],
    setPieceDefence: [
      {title:"Corners",desc:"Their set pieces may be secondary given the open game, but a high-press team often scores from turnovers at set pieces. Recover quickly."},
      {title:"Goal Kicks",desc:"Short goal kicks — do not go long under their press. Build from the back and find the free man."},
    ],
    setPieceAttack: [
      {title:"Corner Routine — Quick Ball",desc:"Their defensive line is aggressive. A quickly taken corner to an unmarked player at the edge of the box catches them off guard."},
      {title:"Free Kick — Ball Over the Top",desc:"A well-delivered free kick over their high line puts your striker in a 1v1. Practice the delivery and the run."},
    ],
    weekPlan: [
      {day:"Tuesday",theme:"Press Resistance",intensity:65,colour:"var(--teal)",drills:["Build-up vs 4-player press","GK short pass under pressure","Third man combination drill"],note:"Technical session. Build comfort on the ball under pressure. Every player receives and plays under a presser."},
      {day:"Wednesday",theme:"Transition Speed",intensity:85,colour:"var(--darkred)",drills:["Press breaking — 5v4 build-up","Counter-attack after breaking press (6v4)","Ball over the top — striker vs high line"],note:"Highest intensity. Practice breaking the press and going immediately. Time the counter — 4 seconds maximum."},
      {day:"Thursday",theme:"Shape Recovery",intensity:55,colour:"#16a34a",drills:["Recovery sprint drills","Defensive shape after turnover","GK distribution patterns"],note:"Focus on what happens when you lose the ball. Recovery speed and shape."},
      {day:"Friday",theme:"Match Prep",intensity:70,colour:"var(--mustard)",drills:["Walk through their press triggers","Practice the 3 press-breaking patterns","11v11 scenario — press vs composure"],note:"Final prep. Walk through exactly where they press and what your response is."},
    ],
    keyBattles: [
      {us:"Your GK",usPos:"GK",them:"Their pressing striker",themThreat:"Forces mistakes under pressure"},
      {us:"Your CDM",usPos:"CDM",them:"Their No.10",themThreat:"Press trigger — activates the whole team"},
      {us:"Your CB",usPos:"CB",them:"Their wide presser",themThreat:"Cuts off the short pass option"},
    ],
    matchdayMessage: "Stay calm. Their press is designed to make you panic. The team that stays composed, plays short and breaks the press wins this match. When you break the press, go immediately — they are at their most vulnerable in the first 3 seconds after the press fails.",
  },

  "possession": {
    colour: "#065f46",
    summary: "They want to control the ball and the tempo. They will make you chase shadows and tire you out. Your shape, patience and transitions win this match.",
    theirPrinciples: [
      "Keep the ball — possession is their weapon and their defence",
      "Short passes, constant movement — never still, never isolated",
      "Patient build-up — they will wait for the right moment",
      "High defensive line — technically excellent defenders who step out with the ball",
      "Press immediately when they lose the ball — win it back quickly",
    ],
    threats: [
      {icon:"🔄",title:"Possession Dominance",desc:"They will have 60-65% of the ball. Your team must be comfortable without the ball for long periods. Mental discipline is the key.",level:"high"},
      {icon:"🎯",title:"Half-space Combinations",desc:"Their most dangerous area is the half-space between your CB and FB. Their No.10 lives there. Must be tracked tightly.",level:"high"},
      {icon:"⚡",title:"Quick Counter-press",desc:"When they lose the ball, they win it back within 6 seconds. Be ready to be pressed immediately after winning possession.",level:"med"},
    ],
    defensiveInstructions: [
      "Hold your shape — do not chase the ball or be dragged out of position",
      "Define your press triggers — only press in 2-3 specific situations",
      "Protect the half-space — the channel between CB and FB must be covered at all times",
      "Do not try to out-possess them — defend deep and hit on the counter",
      "When you win the ball, go vertical immediately before their counter-press arrives",
    ],
    attackingInstructions: [
      "When you win the ball, go direct — 3 passes maximum before a shot or chance",
      "The long ball over their high line is your best weapon — their line is high",
      "Your striker must be ready to run in behind — hold their line",
      "Wide players must be direct — take on the full-back and deliver",
      "Set pieces are your best chance to score — treat every one as critical",
    ],
    setPieceDefence: [
      {title:"Corners",desc:"Their delivery will be accurate. Zonal marking — clear with conviction. No half-clearances."},
      {title:"Free Kicks",desc:"Their dead ball delivery is high quality. Wall discipline is essential. GK must command their area."},
    ],
    setPieceAttack: [
      {title:"Corners",desc:"They defend zonally and with organisation. A quick ball to the near post or a pull-back routine can surprise them."},
      {title:"Free Kicks",desc:"Direct delivery when within range. Their high defensive line means a well-placed ball can cause danger."},
    ],
    weekPlan: [
      {day:"Tuesday",theme:"Defensive Shape",intensity:55,colour:"var(--teal)",drills:["Compact block vs possession team","Press trigger definition session","Counter-attack from deep (4v3)"],note:"Shape first. Define your press triggers. Walk through when to press vs when to hold."},
      {day:"Wednesday",theme:"Counter-attack",intensity:80,colour:"var(--darkred)",drills:["Vertical counter — 3 passes to shot","Ball over the top — striker movement","Wide direct 1v1 duels"],note:"Main physical session. Practice the counter-attack. Speed of transition is everything."},
      {day:"Thursday",theme:"Set Pieces",intensity:55,colour:"var(--mustard)",drills:["Attacking set piece routines","Defensive set piece organisation","Quick restart drills"],note:"Set pieces are your primary scoring threat. Every routine must be perfect."},
      {day:"Friday",theme:"Match Prep",intensity:65,colour:"#16a34a",drills:["Walk through their possession patterns","Define press triggers vs their system","11v11 — defend deep and counter"],note:"Mental and tactical preparation. Know exactly when to press and when to hold."},
    ],
    keyBattles: [
      {us:"Your CDM",usPos:"CDM",them:"Their No.10",themThreat:"Half-space combinations"},
      {us:"Your CB pair",usPos:"CB",them:"Their false 9",themThreat:"Drops deep and drags you out"},
      {us:"Your striker",usPos:"ST",them:"Their CB",themThreat:"Ball-playing — do not let them build"},
    ],
    matchdayMessage: "This match is about mental strength. You will have less of the ball. That is fine. Stay organised, stay patient and wait for your chance. When it comes — take it with quality. One goal from a set piece or a counter can win this match.",
  },

  "direct": {
    colour: "#78350f",
    summary: "They will try to bully you physically, win the second ball and score from set pieces. Your technical quality and defensive organisation beat them.",
    theirPrinciples: [
      "Long ball to the target striker — compete aerially and win the second ball",
      "Physical midfield — win the battle in the middle of the pitch",
      "Set pieces are a primary threat — tall players in both boxes",
      "Deny space by playing direct — no build-up, immediate long ball",
      "Hard working and disciplined — no passengers in a direct team",
    ],
    threats: [
      {icon:"💪",title:"Aerial Threat",desc:"Every long ball, every corner, every free kick is a threat. You must win the first ball to avoid the second ball chaos. Your CBs must dominate aerially.",level:"high"},
      {icon:"🎯",title:"Set Piece Goals",desc:"Their primary scoring method. Near-post runners, far-post hangers, penalty spot arrivals. Every corner is dangerous.",level:"high"},
      {icon:"⚽",title:"Second Ball",desc:"After the long ball is cleared, they win the second ball and counter. Your CDM must position to win the loose ball.",level:"med"},
    ],
    defensiveInstructions: [
      "Win the first ball — if the CB wins the aerial duel, the threat is over",
      "CDM must position for the second ball — do not press the striker, win the loose ball",
      "Never play out of the back under their long ball press — clear it first",
      "Set piece organisation must be perfect — know your man and your zone",
      "Full-backs must deal with physical wingers — do not let them cross",
    ],
    attackingInstructions: [
      "Keep the ball on the floor — deny them the physical duels they want",
      "Short combinations beat their physicality — they cannot track quick one-twos",
      "Play through their midfield, not over it — short and quick is the answer",
      "Their full-backs will be defensive-minded — exploit the wide spaces",
      "Set pieces — you may outscore them at their own game with better delivery",
    ],
    setPieceDefence: [
      {title:"Corners — Aerial Threat",desc:"Man-mark their biggest aerial threats. Zonal marking for the rest. GK must be dominant and claim everything they can reach."},
      {title:"Long Throws",desc:"Treat like a corner. Near-post must be covered. The second ball player holds outside the box."},
    ],
    setPieceAttack: [
      {title:"Corners — Technical Delivery",desc:"Your technical delivery can be superior. Pace the corner delivery away from the keeper. Near-post flick or far-post target."},
      {title:"Free Kicks — Low and Driven",desc:"A low, driven free kick into the near post catches physical defenders off guard — they expect the high ball."},
    ],
    weekPlan: [
      {day:"Tuesday",theme:"Aerial Work",intensity:65,colour:"var(--darkred)",drills:["Aerial duel training — CBs vs strikers","Second ball winning drill","Keep ball under physical pressure (8v4)"],note:"Physical preparation. Aerial duels and second ball. Your CBs must be ready to compete."},
      {day:"Wednesday",theme:"Technical Under Pressure",intensity:75,colour:"var(--teal)",drills:["Short combination patterns","Midfield possession under physical press","Wide exploitation drill"],note:"Technical session under physical pressure. Quick one-twos. Keep it on the floor."},
      {day:"Thursday",theme:"Set Pieces",intensity:55,colour:"var(--mustard)",drills:["Defensive set piece rehearsal","Attacking set piece delivery","Long throw defence drill"],note:"Full set piece session. Both defensive and attacking. Know exactly what they will do."},
      {day:"Friday",theme:"Match Prep",intensity:65,colour:"#16a34a",drills:["Walk through their long ball targets","Second ball positioning session","11v11 — technical vs physical"],note:"Prepare for the physical battle mentally as well as tactically."},
    ],
    keyBattles: [
      {us:"Your CB pairing",usPos:"CB",them:"Their target striker",themThreat:"Aerial dominance and hold-up"},
      {us:"Your CDM",usPos:"CDM",them:"Their physical CM",themThreat:"Second ball and midfield control"},
      {us:"Your FB pair",usPos:"FB",them:"Their physical wingers",themThreat:"Crosses and physicality wide"},
    ],
    matchdayMessage: "They want a physical fight. Don't give it to them. Keep the ball on the floor, play short and quick, and let your technical quality win the battle. Stay disciplined at set pieces — that is where this match can be lost.",
  },

  "counter": {
    colour: "#1e3a5f",
    summary: "They will invite pressure and exploit the space you leave when you attack. Controlled attacking with defensive discipline wins this match.",
    theirPrinciples: [
      "Absorb pressure — sit deep and invite you to attack",
      "Win the ball and go immediately — pace in behind is everything",
      "Never over-commit defensively — always have a runner ready",
      "Their striker makes the run in behind the moment the ball is won",
      "Transitions are their most dangerous moment — and yours",
    ],
    threats: [
      {icon:"🏹",title:"Pace in Behind",desc:"Their counter-attacking striker will run in behind your defensive line every time the ball is won. Never leave your CBs in a 1v1 with pace.",level:"high"},
      {icon:"⚡",title:"Transition Speed",desc:"They go from defence to attack in 3-4 seconds. The space left by your attacking full-backs is the target. One CM must stay.",level:"high"},
      {icon:"🎯",title:"Set Pieces",desc:"If you frustrate their counter, set pieces become their primary threat. Treat every dead ball with maximum defensive attention.",level:"med"},
    ],
    defensiveInstructions: [
      "One CM must always stay — never commit both central midfielders forward",
      "Full-backs can attack but a CB must cover the channel they leave",
      "The defensive line must drop when you lose the ball — no high line on the counter",
      "Do not over-commit in the final third — leave an escape route if the press fails",
      "Speed of defensive recovery after losing possession is non-negotiable",
    ],
    attackingInstructions: [
      "Attack with control — not with recklessness",
      "Wide areas are safer than central overloads — deliver from wide, not through the middle",
      "Maximum 4 players in the attack — 7 must be able to defend immediately",
      "Set pieces — your safest method of creating chances against a counter team",
      "Patience — they want you to rush and leave gaps. Slow the game down.",
    ],
    setPieceDefence: [
      {title:"Quick Restart After Their Corner",desc:"After defending a corner, they restart quickly. Everyone must recover before engaging the ball carrier."},
      {title:"Counter After Their Goal Kick",desc:"Their goal kick goes long immediately. Your defensive line must be aware and not be caught flat-footed."},
    ],
    setPieceAttack: [
      {title:"Corners — Safety First",desc:"Designate a defensive player to not attack the corner. They hold the counter-attack threat. Attacking corners must have cover."},
      {title:"Free Kicks — Direct",desc:"Direct free kicks avoid the counter-attack risk of a missed corner. Deliver accurately and with purpose."},
    ],
    weekPlan: [
      {day:"Tuesday",theme:"Controlled Attack",intensity:60,colour:"var(--teal)",drills:["Controlled attack — max 4 forward","Wide crossing drill — avoid counter exposure","Defensive shape when attacking"],note:"Establish the attacking rules. How many go forward. Who covers. Walk through the scenarios."},
      {day:"Wednesday",theme:"Defensive Transition",intensity:80,colour:"var(--darkred)",drills:["Drop on turnover drill","Recovery sprint — counter-attack prevention","1v1 defensive duel vs pace"],note:"Highest intensity. Practice recovering when the ball is lost. Timed — must be in shape before they reach halfway."},
      {day:"Thursday",theme:"Set Pieces",intensity:55,colour:"var(--mustard)",drills:["Attacking corners with defensive coverage","Free kick delivery — direct","Counter-attack prevention after set pieces"],note:"Set pieces with counter-attack coverage built in. Your designated defensive player holds."},
      {day:"Friday",theme:"Match Prep",intensity:68,colour:"#16a34a",drills:["Walk through their counter patterns","Identify their pace threat — assign cover","11v11 — controlled attack vs counter"],note:"Know their counter-attack pattern. Who runs, from where, and what triggers it."},
    ],
    keyBattles: [
      {us:"Your FB pair",usPos:"FB",them:"Their pacey winger",themThreat:"Exploits the space when FB attacks"},
      {us:"Your holding CM",usPos:"CDM",them:"Their counter-attack runner",themThreat:"Third man run in behind midfield"},
      {us:"Your CB",usPos:"CB",them:"Their striker",themThreat:"Pure pace in behind — 1v1 race"},
    ],
    matchdayMessage: "Control your attacking instinct. They want you to over-commit. Attack with discipline, cover your tracks and be patient. One goal from a set piece or a controlled chance wins this match. Do not gift them a counter-attack through recklessness.",
  },

  "mid-block": {
    colour: "#3730a3",
    summary: "They press in organised waves — not constantly, but aggressively when triggered. They want to disrupt your rhythm in the transition zones.",
    theirPrinciples: [
      "Press in waves from the mid-block — not constant, but sudden and coordinated",
      "Define specific press triggers — your goalkeeper, your slow CB, the isolated full-back",
      "Win the ball in the transition zone — between your defence and midfield",
      "Compact shape when not pressing — hard to play through",
      "Quick vertical ball when possession is won — attack before you recover",
    ],
    threats: [
      {icon:"🎯",title:"Wave Pressing",desc:"Their press comes in waves. You cannot predict exactly when it will come — only where from. Technical players must be ready at all times.",level:"high"},
      {icon:"⚡",title:"Transition Zone Wins",desc:"The space between your lines is where they win the ball. Your midfield must receive and play quickly — no dwelling.",level:"high"},
      {icon:"🔄",title:"Shape Recovery",desc:"They recover quickly when their press fails. Do not expect open space after breaking the press — they recover fast.",level:"med"},
    ],
    defensiveInstructions: [
      "Recognise their press trigger pattern — what activates their press",
      "The GK must be comfortable — the back pass is always an option",
      "Your CDM must show for the ball in the press zone — be available",
      "When the press comes, use the third man combination to escape",
      "Do not dwell in the transition zone — receive and play immediately",
    ],
    attackingInstructions: [
      "Play through the press — short combinations, not long balls into their block",
      "Width is your friend — get the ball wide before they can press centrally",
      "The third man combination is your primary press-breaker",
      "When the press fails, go immediately — they are transitioning back to shape",
      "Set pieces — like all organised teams, their set piece defence can be exploited",
    ],
    setPieceDefence: [
      {title:"Quick Restart",desc:"After defending their press and winning possession, they restart quickly. Your set piece defence must also recover rapidly."},
    ],
    setPieceAttack: [
      {title:"Corners — Two Variations",desc:"A mid-block team defends corners with organisation. Have two different routines — one for man marking, one for zonal."},
      {title:"Free Kicks — Quick Variation",desc:"A quickly taken free kick before they set their block can catch them disorganised. Have one player ready to go immediately."},
    ],
    weekPlan: [
      {day:"Tuesday",theme:"Third Man Combinations",intensity:60,colour:"var(--teal)",drills:["Third man combination drill (groups of 4)","Wide release under press","GK reset and restart drill"],note:"Technical session. Practice the press-breaking combinations. Third man is the key."},
      {day:"Wednesday",theme:"Press Recognition",intensity:80,colour:"var(--darkred)",drills:["Pattern recognition — identify the trigger","Possession under wave press (8v8)","Transition after breaking — go immediately"],note:"Main session. Identify their press triggers in a game scenario. React before the press arrives."},
      {day:"Thursday",theme:"Set Pieces",intensity:55,colour:"var(--mustard)",drills:["Two corner routines — vs man and zonal","Quick free kick variation","Defensive set piece organisation"],note:"Set pieces. Two offensive routines — one for each defensive system they may use."},
      {day:"Friday",theme:"Match Prep",intensity:68,colour:"#16a34a",drills:["Walk through their press triggers","Practice 3 specific press-breaking patterns","11v11 — rhythm and composure"],note:"Know exactly when they press and from where. Practice staying calm in the press zone."},
    ],
    keyBattles: [
      {us:"Your GK",usPos:"GK",them:"Their press trigger striker",themThreat:"Activates the whole team's press"},
      {us:"Your CDM",usPos:"CDM",them:"Their No.10 presser",themThreat:"Cuts off the CDM option to force backwards"},
      {us:"Your No.10",usPos:"CAM",them:"Their CDM",themThreat:"Physical screening in the transition zone"},
    ],
    matchdayMessage: "Stay calm. Their press is wave-based — it is not constant. The periods between waves are your opportunity. Find the third man combination, break the press and go immediately. The team that stays composed and plays through the press wins.",
  },
};

/* Formation dots */
const FORM_DOTS = {
  "4-4-2":   [{x:50,y:88},{x:22,y:68},{x:38,y:72},{x:62,y:72},{x:78,y:68},{x:82,y:48},{x:62,y:48},{x:38,y:48},{x:18,y:48},{x:62,y:22},{x:38,y:22}],
  "4-3-3":   [{x:50,y:88},{x:22,y:70},{x:38,y:74},{x:62,y:74},{x:78,y:70},{x:68,y:50},{x:50,y:46},{x:32,y:50},{x:80,y:25},{x:50,y:18},{x:20,y:25}],
  "4-2-3-1": [{x:50,y:88},{x:22,y:70},{x:38,y:74},{x:62,y:74},{x:78,y:70},{x:60,y:57},{x:40,y:57},{x:78,y:36},{x:50,y:32},{x:22,y:36},{x:50,y:16}],
  "3-5-2":   [{x:50,y:88},{x:25,y:74},{x:50,y:77},{x:75,y:74},{x:88,y:52},{x:65,y:50},{x:50,y:48},{x:35,y:50},{x:12,y:52},{x:62,y:20},{x:38,y:20}],
  "3-4-3":   [{x:50,y:88},{x:25,y:74},{x:50,y:77},{x:75,y:74},{x:85,y:50},{x:62,y:50},{x:38,y:50},{x:15,y:50},{x:80,y:22},{x:50,y:16},{x:20,y:22}],
  "5-3-2":   [{x:50,y:88},{x:88,y:65},{x:68,y:74},{x:50,y:77},{x:32,y:74},{x:12,y:65},{x:68,y:48},{x:50,y:46},{x:32,y:48},{x:62,y:20},{x:38,y:20}],
  "4-1-4-1": [{x:50,y:88},{x:22,y:70},{x:38,y:74},{x:62,y:74},{x:78,y:70},{x:50,y:60},{x:82,y:44},{x:62,y:44},{x:38,y:44},{x:18,y:44},{x:50,y:18}],
  "4-4-2 Diamond": [{x:50,y:88},{x:22,y:70},{x:38,y:74},{x:62,y:74},{x:78,y:70},{x:50,y:60},{x:30,y:48},{x:70,y:48},{x:50,y:36},{x:62,y:20},{x:38,y:20}],
};

function ini(n){return n&&n.split(" ").map(w=>w[0]).join("")||"?";}
function posGroupColor(g){return g==="gk"?"#f59e0b":g==="def"?"var(--darkred)":g==="mid"?"var(--teal)":"#16a34a";}
function posGroup(pos){
  if(pos==="GK") return "gk";
  if(["CB","RB","LB","WB"].includes(pos)) return "def";
  if(["CDM","CM","CAM","RM","LM"].includes(pos)) return "mid";
  return "att";
}

/* ══════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════ */
export default function OppositionDossier({squad=[], onNavigate=null}) {
  const [oppName,    setOppName]    = useState("");
  const [oppStyle,   setOppStyle]   = useState(null);
  const [oppForm,    setOppForm]    = useState("4-4-2");
  const [matchDate,  setMatchDate]  = useState("");
  const [competition,setCompetition]= useState("");
  const [venue,      setVenue]      = useState("home");
  const [threats,    setThreats]    = useState([]);
  const [dossier,    setDossier]    = useState(null);
  const [savedDossiers, setSavedDossiers] = useState(()=>loadFromStorage("dossiers",[]));
  const [view,       setView]       = useState("builder"); // builder | dossier
  const [realForm,   setRealForm]   = useState(null);   // real results from football-data.org
  const [formBrief,  setFormBrief]  = useState(null);   // built from buildOppositionBrief
  const [apiKey,     setApiKey]     = useState(()=>loadFromStorage("fd_api_key",""));
  const [fetchingForm, setFetchingForm] = useState(false);

  const toggleThreat = (t) => setThreats(prev=>prev.includes(t)?prev.filter(x=>x!==t):[...prev,t]);

  const toast = useToast();
  const [generating, setGenerating] = useState(false);
  const [genProgress, setGenProgress] = useState("");

  const generateDossier = async () => {
    if(!oppName || !oppStyle) return;
    setGenerating(true);
    setGenProgress("Analysing opposition style...");

    const styleLabel = OPP_STYLES.find(s=>s.id===oppStyle)?.label || oppStyle;
    const squadSummary = squad.length > 0
      ? squad.slice(0,11).map(p=>`${p.name} (${p.pos}, OVR ${p.overall})`).join(", ")
      : "Squad not specified";

    try {
      const formSection = realForm
        ? `\nRECENT RESULTS (last ${realForm.trim().split('\n').filter(Boolean).length} matches):\n${realForm}\n\nUse these ACTUAL results to inform the threat levels, form description and matchday message.`
        : "";

      const prompt = `You are a professional football analyst. Generate a detailed pre-match opposition dossier for:

OPPONENT: ${oppName}
FORMATION: ${oppForm || "Unknown"}
STYLE: ${styleLabel}
MATCH: ${competition||"Match"} · ${venue==="home"?"Home":"Away"} · ${matchDate||"Date TBD"}
ADDITIONAL NOTES: ${oppNotes||"None provided"}
OUR SQUAD: ${squadSummary}${formSection}

Respond ONLY with valid JSON matching this exact structure (no markdown, no explanation):
{
  "theirPrinciples": ["principle 1", "principle 2", "principle 3", "principle 4", "principle 5"],
  "threats": [
    {"title":"Threat name","desc":"Description","level":"high","instruction":"How to counter this"},
    {"title":"Threat name","desc":"Description","level":"medium","instruction":"How to counter this"},
    {"title":"Threat name","desc":"Description","level":"low","instruction":"How to counter this"}
  ],
  "defensiveInstructions": ["instruction 1","instruction 2","instruction 3","instruction 4","instruction 5"],
  "attackingInstructions": ["instruction 1","instruction 2","instruction 3","instruction 4","instruction 5"],
  "setPieceNotes": ["note 1","note 2","note 3"],
  "keyBattles": [
    {"ourPlayer":"Position or player","ourRole":"Our role","theirPlayer":"Their key player type","themThreat":"Their threat","instruction":"Key instruction"},
    {"ourPlayer":"Position or player","ourRole":"Our role","theirPlayer":"Their key player type","themThreat":"Their threat","instruction":"Key instruction"}
  ],
  "weekPlan": [
    {"day":"Mon","theme":"Theme","drills":["drill 1","drill 2"],"note":"Coaching note"},
    {"day":"Tue","theme":"Theme","drills":["drill 1","drill 2"],"note":"Coaching note"},
    {"day":"Wed","theme":"Theme","drills":["drill 1","drill 2"],"note":"Coaching note"},
    {"day":"Thu","theme":"Theme","drills":["drill 1","drill 2"],"note":"Coaching note"}
  ],
  "matchdayMessage": "A 2-3 sentence motivational message for the players focused on this specific opponent and match context"
}

Make everything specific to ${oppName} playing ${styleLabel} in a ${oppForm||"standard"} formation. Use realistic football coaching language.`;

      setGenProgress("Generating tactical report...");

      const res = await fetch("/api/claude", {
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body: JSON.stringify({
          system:"You are a professional football analyst. Always respond with valid JSON only — no markdown code blocks, no explanation, just the raw JSON object.",
          messages:[{role:"user",content:prompt}],
          max_tokens:1500,
        })
      });

      if(!res.ok) throw new Error(`API error ${res.status}`);
      const result = await res.json();
      const rawText = result.content?.map(c=>c.text||"").join("") || "";
      const clean = rawText.replace(/```json|```/g,"").trim();
      const data = JSON.parse(clean);

      const newDossier = {
        id: Date.now(),
        oppName, oppStyle, oppForm, matchDate, competition, venue, threats,
        data,
        aiGenerated: true,
        generatedAt: new Date().toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"}),
      };
      setDossier(newDossier);
      const updated = [newDossier, ...savedDossiers.slice(0,9)];
      setSavedDossiers(updated);
      await saveDossierToCloud(newDossier);
      setView("dossier");
      toast && toast(`Dossier generated for ${oppName}`, "success");
    } catch(e) {
      console.error("Dossier generation failed:", e);
      // Fall back to template if AI fails
      const data = DOSSIER_DATA[oppStyle] || DOSSIER_DATA["possession"];
      const newDossier = {
        id: Date.now(),
        oppName, oppStyle, oppForm, matchDate, competition, venue, threats,
        data, aiGenerated: false,
        generatedAt: new Date().toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"}),
      };
      setDossier(newDossier);
      const updated = [newDossier, ...savedDossiers.slice(0,9)];
      setSavedDossiers(updated);
      await saveDossierToCloud(newDossier);
      setView("dossier");
      toast && toast("AI unavailable — using template dossier", "warning");
    } finally {
      setGenerating(false);
      setGenProgress("");
    }
  };

  const exportDossier = () => {
    if(!dossier) return;
    const d = dossier.data;
    const lines = [
      `FOOTY ANALYST — PRE-MATCH DOSSIER`,
      `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
      `OPPONENT: ${dossier.oppName}`,
      `FORMATION: ${dossier.oppForm}  |  STYLE: ${OPP_STYLES.find(s=>s.id===dossier.oppStyle)?.label}`,
      `MATCH: ${dossier.competition||"Match"} · ${dossier.matchDate||"TBD"} · ${dossier.venue==="home"?"Home":"Away"}`,
      `Generated: ${dossier.generatedAt}`,
      `\nTHEIR PRINCIPLES`,`─────────────────`,
      ...d.theirPrinciples.map(p=>`• ${p}`),
      `\nTHEIR THREATS`,`──────────────`,
      ...d.threats.map(t=>`[${t.level.toUpperCase()}] ${t.title}: ${t.desc}`),
      `\nOUR DEFENSIVE INSTRUCTIONS`,`───────────────────────────`,
      ...d.defensiveInstructions.map(i=>`→ ${i}`),
      `\nOUR ATTACKING INSTRUCTIONS`,`───────────────────────────`,
      ...d.attackingInstructions.map(i=>`→ ${i}`),
      `\nMATCH DAY MESSAGE`,`─────────────────`,
      d.matchdayMessage,
      `\nWEEK TRAINING PLAN`,`───────────────────`,
      ...d.weekPlan.map(w=>`${w.day}: ${w.theme}\n  ${w.drills.join(" | ")}\n  ${w.note}`),
    ];
    if(squad.length>0){
      lines.push(`\nPLAYER BRIEFINGS`,`────────────────`);
      squad.forEach((p,i)=>{
        const battle = d.keyBattles[i%d.keyBattles.length];
        lines.push(`${p.name} (${p.pos}): Focus on ${battle?.themThreat||"their role"}`);
      });
    }
    const blob = new Blob([lines.join("\n")],{type:"text/plain"});
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href=url; a.download=`dossier-${dossier.oppName.replace(/\s+/g,"-")}.txt`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <style>{OD_CSS}</style>
      <div className="od-wrap">
        {/* HERO */}
        <div className="od-hero">
          <div className="od-hero-inner">
            <div>
              <div className="od-hero-title">Pre-match Opposition Dossier</div>
              <div className="od-hero-sub">Build a complete tactical intelligence report on your next opponent. Threats, instructions, a 4-day training plan and individual player briefings — all generated in seconds.</div>
            </div>
            <div style={{display:"flex",gap:"0.5rem",alignItems:"flex-start"}}>
              <button onClick={()=>setView("builder")} style={{padding:"0.48rem 1rem",fontFamily:"'DM Sans',sans-serif",fontSize:"0.68rem",fontWeight:800,letterSpacing:"1.5px",textTransform:"uppercase",background:view==="builder"?"var(--mustard)":"transparent",color:view==="builder"?"var(--ink)":"rgba(255,255,255,0.7)",border:"1.5px solid rgba(255,255,255,0.25)",cursor:"pointer"}}>Build New</button>
              {dossier&&<button onClick={()=>setView("dossier")} style={{padding:"0.48rem 1rem",fontFamily:"'DM Sans',sans-serif",fontSize:"0.68rem",fontWeight:800,letterSpacing:"1.5px",textTransform:"uppercase",background:view==="dossier"?"var(--mustard)":"transparent",color:view==="dossier"?"var(--ink)":"rgba(255,255,255,0.7)",border:"1.5px solid rgba(255,255,255,0.25)",cursor:"pointer"}}>View Dossier</button>}
            </div>
          </div>
        </div>

        <div className="od-layout">
          {/* SIDEBAR */}
          <div className="od-sidebar">
            {/* Match info */}
            <div className="od-sb-section">
              <div className="od-sb-title">Match Information</div>
              <label className="od-label">Opposition Name *</label>
              <input className="od-input" placeholder="e.g. North United" value={oppName} onChange={e=>setOppName(e.target.value)}/>
              <label className="od-label">Match Date</label>
              <input className="od-input" type="date" value={matchDate} onChange={e=>setMatchDate(e.target.value)}/>
              <label className="od-label">Competition</label>
              <select className="od-input" value={competition} onChange={e=>setCompetition(e.target.value)}>
                <option value="">Select competition...</option>
                {COMPETITIONS.map(c=><option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
              <label className="od-label">Venue</label>
              <select className="od-select" value={venue} onChange={e=>setVenue(e.target.value)}>
                <option value="home">Home</option>
                <option value="away">Away</option>
                <option value="neutral">Neutral</option>
              </select>
            </div>

            {/* Real form data panel */}
            <div className="od-sb-section" style={{background:"rgba(46,125,122,0.06)",border:"1px solid var(--teal4)",padding:"0.85rem"}}>
              <div className="od-sb-title" style={{color:"var(--teal)"}}>📊 Real Form Data</div>
              <div style={{fontSize:"0.65rem",color:"var(--muted)",marginBottom:"0.65rem",lineHeight:1.6}}>
                Paste recent results for {oppName||"the opponent"} below to generate a real form brief. One result per line:<br/>
                <code style={{fontSize:"0.6rem",background:"var(--cream2)",padding:"0.1rem 0.35rem"}}>Win 2-1 vs Chester FC (H)</code>
              </div>
              <textarea
                className="od-input"
                placeholder={"Win 2-1 vs Chester FC (H)\nLoss 0-1 vs Altrincham FC (A)\nDraw 1-1 vs Blyth Spartans (H)\n..."}
                rows={5}
                style={{resize:"vertical",fontFamily:"'DM Mono',monospace",fontSize:"0.65rem",lineHeight:1.7}}
                value={realForm||""}
                onChange={e=>setRealForm(e.target.value)}
              />
              {realForm && (
                <div style={{marginTop:"0.65rem"}}>
                  {(() => {
                    // Parse pasted results into a brief
                    const lines = realForm.trim().split('\n').filter(Boolean);
                    const wins   = lines.filter(l=>l.toLowerCase().startsWith('win')||l.toLowerCase().includes(' w ')).length;
                    const losses = lines.filter(l=>l.toLowerCase().startsWith('loss')||l.toLowerCase().includes(' l ')).length;
                    const draws  = lines.filter(l=>l.toLowerCase().startsWith('draw')||l.toLowerCase().includes(' d ')).length;
                    const total  = lines.length;
                    const pts    = wins*3 + draws;
                    const form5  = lines.slice(0,5).map(l=>{
                      const lo = l.toLowerCase();
                      if(lo.startsWith('win')||lo.includes(' w ')) return 'W';
                      if(lo.startsWith('loss')||lo.includes(' l ')) return 'L';
                      return 'D';
                    }).join('');
                    return (
                      <div style={{display:"flex",gap:"0.65rem",flexWrap:"wrap",alignItems:"center"}}>
                        <div style={{fontFamily:"'DM Mono',monospace",fontSize:"0.72rem",letterSpacing:"2px"}}>
                          {form5.split('').map((c,i)=>(
                            <span key={i} style={{
                              display:"inline-block",width:20,height:20,lineHeight:"20px",textAlign:"center",
                              marginRight:2,fontSize:"0.6rem",fontWeight:800,
                              background:c==='W'?"var(--green)":c==='L'?"var(--darkred)":"var(--muted)",
                              color:"white"
                            }}>{c}</span>
                          ))}
                        </div>
                        <div style={{fontSize:"0.65rem",color:"var(--muted)"}}>
                          {total} matches · {wins}W {draws}D {losses}L · {pts}pts
                          {pts/total < 1.0 && <span style={{color:"var(--darkred)",fontWeight:700,marginLeft:"0.5rem"}}>⚠ Poor form</span>}
                          {pts/total >= 2.0 && <span style={{color:"var(--green)",fontWeight:700,marginLeft:"0.5rem"}}>↑ Strong form</span>}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* Their style */}
            <div className="od-sb-section">
              <div className="od-sb-title">Their Playing Style *</div>
              <div className="od-style-grid">
                {OPP_STYLES.map(s=>(
                  <button key={s.id} className={`od-style-btn ${oppStyle===s.id?"on":""}`} onClick={()=>setOppStyle(s.id)}>
                    {s.icon} {s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Their formation */}
            <div className="od-sb-section">
              <div className="od-sb-title">Their Formation</div>
              <select className="od-select" value={oppForm} onChange={e=>setOppForm(e.target.value)}>
                {OPP_FORMATIONS.map(f=><option key={f}>{f}</option>)}
              </select>
            </div>

            {/* Known threats */}
            <div className="od-sb-section">
              <div className="od-sb-title">Known Threats</div>
              <div className="od-threat-list">
                {THREAT_OPTIONS.map(t=>(
                  <button key={t} className={`od-threat-btn ${threats.includes(t)?"on":""}`} onClick={()=>toggleThreat(t)}>
                    <div className="od-threat-dot"/>
                    <span className="od-threat-label">{t}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Generate */}
            <div style={{padding:"1rem",borderTop:"2px solid var(--rule)"}}>
              <button className="od-generate" onClick={generateDossier} disabled={!oppName||!oppStyle||generating}>
                {generating?(genProgress||"Generating..."):!oppName?"Enter opponent name →":!oppStyle?"Select their style →":"✦ Generate AI Dossier →"}
              </button>
              {generating&&<div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.68rem",color:"var(--muted)",fontStyle:"italic",marginTop:"0.5rem",textAlign:"center"}}>Claude is analysing {oppName} — this takes about 10 seconds...</div>}
            </div>

            {/* Saved */}
            {savedDossiers.length>0&&(
              <div className="od-sb-section">
                <div className="od-sb-title">Previous Dossiers</div>
                {savedDossiers.slice(0,5).map(d=>(
                  <div key={d.id} className="od-saved-item" onClick={()=>{setDossier(d);setView("dossier");}}>
                    <div style={{flex:1}}>
                      <div className="od-saved-name">{d.oppName}</div>
                      <div className="od-saved-date">{d.competition||"Match"} · {d.generatedAt}</div>
                    </div>
                    <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.6rem",color:"var(--muted)"}}>→</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* MAIN CONTENT */}
          <div className="od-main">
            {view==="builder"&&!dossier&&(
              <div className="od-empty">
                <div className="od-empty-icon">🎯</div>
                <div className="od-empty-title">Build Your Dossier</div>
                <div className="od-empty-sub">Fill in the match details and select the opposition's playing style on the left. Click "Generate Full Dossier" and get a complete tactical intelligence report — threats, instructions, a 4-day training plan and individual player briefings.</div>
              </div>
            )}

            {view==="dossier"&&dossier&&(()=>{
              const d = dossier.data;
              const style = OPP_STYLES.find(s=>s.id===dossier.oppStyle);
              const dots = FORM_DOTS[dossier.oppForm]||FORM_DOTS["4-4-2"];
              return(
                <div className="od-content">
                  {/* Dossier header */}
                  <div className="od-dossier-header">
                    <div className="od-dossier-vs">Pre-match Intelligence — {dossier.competition||"Match"} · {dossier.venue==="home"?"Home":"Away"}</div>
                    <div className="od-dossier-opponent">{dossier.oppName}</div>
                    <div className="od-dossier-meta">{dossier.matchDate&&`${dossier.matchDate} · `}{style?.icon} {style?.label} · {dossier.oppForm} · Generated {dossier.generatedAt}</div>
                    <div className="od-dossier-tags">
                      <span className="od-dossier-tag">{dossier.oppForm}</span>
                      <span className="od-dossier-tag">{style?.label}</span>
                      {dossier.threats.map((t,i)=><span key={i} className="od-dossier-tag danger">{t}</span>)}
                    </div>
                  </div>

                  {/* Summary + Formation */}
                  <div className="od-grid-2">
                    <div className="od-panel">
                      <div className="od-panel-title">Intelligence Summary <span className="od-chip od-chip-red">Scout Report</span></div>
                      <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.85rem",fontWeight:300,color:"var(--ink2)",lineHeight:1.8,fontStyle:"italic",borderLeft:"3px solid var(--darkred)",paddingLeft:"0.85rem",marginBottom:"1rem"}}>{d.summary}</div>
                      <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.6rem",fontWeight:800,letterSpacing:"2px",textTransform:"uppercase",color:"var(--muted)",marginBottom:"0.5rem"}}>Their Principles</div>
                      {d.theirPrinciples.map((p,i)=>(
                        <div key={i} className="od-instruction">{p}</div>
                      ))}
                    </div>
                    <div className="od-panel">
                      <div className="od-panel-title">Their Formation <span className="od-chip od-chip-red">{dossier.oppForm}</span></div>
                      <div className="od-pitch">
                        <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{opacity:0.18}}>
                          <rect x="5" y="5" width="90" height="90" fill="none" stroke="white" strokeWidth=".5"/>
                          <line x1="5" y1="50" x2="95" y2="50" stroke="white" strokeWidth=".4"/>
                          <circle cx="50" cy="50" r="12" fill="none" stroke="white" strokeWidth=".4"/>
                          <rect x="25" y="5" width="50" height="16" fill="none" stroke="white" strokeWidth=".4"/>
                          <rect x="25" y="79" width="50" height="16" fill="none" stroke="white" strokeWidth=".4"/>
                        </svg>
                        {dots.map((dot,i)=>(
                          <div key={i} className="od-pitch-dot" style={{left:`${dot.x}%`,top:`${dot.y}%`}}>
                            <div className="od-pitch-dot-inner them">
                              <span style={{fontSize:"0.48rem",letterSpacing:"0.5px"}}>{i===0?"GK":""}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.6rem",fontWeight:800,letterSpacing:"2px",textTransform:"uppercase",color:"var(--muted)",marginBottom:"0.5rem"}}>Key Positional Battles</div>
                      {d.keyBattles.map((b,i)=>(
                        <div key={i} className="od-battle">
                          <div className="od-battle-player">
                            <div className="od-battle-name">{b.us}</div>
                            <div className="od-battle-pos">{b.usPos}</div>
                          </div>
                          <div className="od-battle-vs">VS</div>
                          <div className="od-battle-opp">
                            <div className="od-battle-opp-name">{b.them}</div>
                            <div className="od-battle-opp-threat">{b.themThreat}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Threats */}
                  <div className="od-panel" style={{marginBottom:"1rem"}}>
                    <div className="od-panel-title">Threat Assessment <span className="od-chip od-chip-red">{d.threats.length} Key Threats</span></div>
                    {d.threats.map((t,i)=>(
                      <div key={i} className="od-threat-item">
                        <div className="od-threat-icon">{t.icon}</div>
                        <div className="od-threat-content">
                          <div className="od-threat-title">{t.title}</div>
                          <div className="od-threat-desc">{t.desc}</div>
                        </div>
                        <span className={`od-threat-level ${t.level}`}>{t.level}</span>
                      </div>
                    ))}
                    {dossier.threats.length>0&&(
                      <div style={{marginTop:"0.75rem",paddingTop:"0.75rem",borderTop:"1px solid var(--rule2)"}}>
                        <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.6rem",fontWeight:800,letterSpacing:"2px",textTransform:"uppercase",color:"var(--muted)",marginBottom:"0.4rem"}}>Scout-identified Threats</div>
                        <div style={{display:"flex",gap:"0.35rem",flexWrap:"wrap"}}>
                          {dossier.threats.map((t,i)=><span key={i} className="od-dossier-tag danger" style={{border:"1.5px solid var(--darkred)",color:"var(--darkred)",padding:"0.12rem 0.45rem",fontSize:"0.6rem",fontWeight:800,letterSpacing:"1.5px",textTransform:"uppercase"}}>{t}</span>)}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Instructions */}
                  <div className="od-grid-2">
                    <div className="od-panel">
                      <div className="od-panel-title">Defensive Instructions <span className="od-chip od-chip-red">Out of Possession</span></div>
                      {d.defensiveInstructions.map((inst,i)=>(
                        <div key={i} className="od-instruction defensive">{inst}</div>
                      ))}
                    </div>
                    <div className="od-panel">
                      <div className="od-panel-title">Attacking Instructions <span className="od-chip od-chip-teal">In Possession</span></div>
                      {d.attackingInstructions.map((inst,i)=>(
                        <div key={i} className="od-instruction attacking">{inst}</div>
                      ))}
                    </div>
                  </div>

                  {/* Set pieces */}
                  <div className="od-grid-2" style={{marginTop:"1rem"}}>
                    <div className="od-panel">
                      <div className="od-panel-title">Set Piece Defence <span className="od-chip od-chip-red">Defensive Dead Balls</span></div>
                      {d.setPieceDefence.map((s,i)=>(
                        <div key={i} className="od-setpiece">
                          <div className="od-setpiece-title">{s.title}</div>
                          <div className="od-setpiece-desc">{s.desc}</div>
                        </div>
                      ))}
                    </div>
                    <div className="od-panel">
                      <div className="od-panel-title">Set Piece Attack <span className="od-chip od-chip-teal">Our Dead Balls</span></div>
                      {d.setPieceAttack.map((s,i)=>(
                        <div key={i} className="od-setpiece" style={{borderLeftColor:"var(--teal)"}}>
                          <div className="od-setpiece-title">{s.title}</div>
                          <div className="od-setpiece-desc">{s.desc}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* 4-day training plan */}
                  <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"1rem",letterSpacing:"2px",color:"var(--ink)",margin:"1.2rem 0 0.75rem"}}>
                    4-Day Preparation Plan — {dossier.oppName}
                  </div>
                  <div className="od-week">
                    {d.weekPlan.map((day,i)=>(
                      <div key={i} className="od-day">
                        <div className="od-day-head">
                          <div className="od-day-name">{day.day}</div>
                          <div className="od-day-theme">{day.theme}</div>
                        </div>
                        <div className="od-day-body">
                          {day.drills.map((drill,j)=>(
                            <div key={j} className="od-day-drill">{drill}</div>
                          ))}
                          <div className="od-day-note">{day.note}</div>
                          <div className="od-intensity-bar" style={{background:day.colour,opacity:0.7}}/>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Player briefings */}
                  {squad.length>0&&(
                    <div className="od-panel" style={{marginBottom:"1rem"}}>
                      <div className="od-panel-title">
                        Player Briefings — {squad.length} Players
                        <span className="od-chip od-chip-gold">Match Day</span>
                      </div>
                      {squad.map((p,i)=>{
                        const battle = d.keyBattles[i%d.keyBattles.length];
                        const instruction = i<d.defensiveInstructions.length
                          ? d.defensiveInstructions[i]
                          : d.attackingInstructions[i-d.defensiveInstructions.length]||d.matchdayMessage;
                        return(
                          <div key={p.id||i} className="od-player-briefing">
                            <div className="od-pb-av" style={{background:posGroupColor(p.group||posGroup(p.pos))}}>{ini(p.name)}</div>
                            <div className="od-pb-info">
                              <div className="od-pb-name">{p.name}</div>
                              <span className={`od-pb-pos sp-pos-badge ${p.group||posGroup(p.pos)}`}>{p.pos}</span>
                              <div className="od-pb-task">{instruction}</div>
                              <div className="od-pb-focus">
                                {["def","gk"].includes(p.group||posGroup(p.pos))
                                  ? `Watch: ${battle?.them||"their attacking threat"} — ${battle?.themThreat||"main threat"}`
                                  : `Key task: ${d.attackingInstructions[i%d.attackingInstructions.length]}`}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Matchday message */}
                  <div style={{background:"var(--darkred)",border:"2px solid var(--ink)",padding:"1.2rem 1.4rem",marginBottom:"1rem"}}>
                    <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"0.75rem",letterSpacing:"3px",color:"rgba(255,255,255,0.5)",marginBottom:"0.4rem"}}>MATCH DAY MESSAGE — {dossier.oppName.toUpperCase()}</div>
                    <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.88rem",fontWeight:400,color:"var(--white)",lineHeight:1.8}}>{d.matchdayMessage}</div>
                  </div>

                  {/* Export */}
                  <button className="od-export" onClick={exportDossier}>
                    ⬇ Export as Text File
                  </button>
                  <ExportButton onClick={()=>exportDossierPDF(dossier,squad)} label="Export PDF / Print"/>
                </div>
              );
            })()}
          </div>
        </div>
      </div>
    </>
  );
}
